/**
 * Helpers for extracting and formatting shipment display data.
 * Used by ShipmentCard to transform GraphQL shipment/order data into display-ready values.
 */
import { GraphQL } from "@rpi/openapi-api";
import { formatUtcCalendarDay } from "../../../../utils/date.util";

type Order = NonNullable<GraphQL.OrderShipmentsQuery["orderByIdentifier"]>;
type Shipment = NonNullable<Order["shipments"]>[number];
type Destination = NonNullable<Order["destination"]>;
type Address = NonNullable<Destination["address"]>;
type ShipmentItems = NonNullable<Shipment["orderItems"]>;

/** Shipping method, tracking number, and optional tracking link for one shipment card. */
export interface ShipmentMethodView {
  method: string;
  trackingNumber: string | null;
  trackingUrl: string | null;
}

/** Display-ready data for one shipment card, mapped from either a shipped Shipment or a pending Destination. */
export interface ShipmentCardView {
  key: string;
  address: Address | undefined;
  shippingMethod: ShipmentMethodView;
  expectedShipDate: string;
  estimatedDeliveryDate: string;
  items: ShipmentItems;
}

export type { Address };

const NOT_PROVIDED = "-";

/**
 * Builds the cards for the Shipments tab: one per shipment once shipped, otherwise a single
 * pending card from the order destination. Empty when the order has neither.
 */
export function toShipmentCardViews(order: Order): ShipmentCardView[] {
  const shipments = order.shipments ?? [];
  if (shipments.length > 0) {
    return shipments.map(toShipmentCardView);
  }
  if (order.destination) {
    return [toPendingShipmentCardView(order.destination)];
  }
  return [];
}

/** Builds the card view for a shipped shipment. */
export function toShipmentCardView(shipment: Shipment): ShipmentCardView {
  return {
    key: `shipment-${shipment.id}`,
    address: shipment.destination?.address ?? undefined,
    shippingMethod: toShipmentMethodView(shipment),
    expectedShipDate: formatUtcCalendarDay(shipment.destination?.expectedShipDate),
    estimatedDeliveryDate: formatEstimatedDeliveryDate(shipment.destination?.shippingDetails),
    items: shipment.orderItems ?? [],
  };
}

/** Builds the card view for a destination that has not shipped yet: no tracking, no items. */
export function toPendingShipmentCardView(destination: Destination): ShipmentCardView {
  return {
    key: `destination-${destination.id}`,
    address: destination.address ?? undefined,
    shippingMethod: {
      method: readMethodName(destination.shippingDetails) ?? NOT_PROVIDED,
      trackingNumber: null,
      trackingUrl: null,
    },
    expectedShipDate: formatUtcCalendarDay(destination.expectedShipDate),
    estimatedDeliveryDate: formatEstimatedDeliveryDate(destination.shippingDetails),
    items: [],
  };
}

function getTrackingNumber(shipment: Shipment): string | null {
  return shipment.trackingNumber ?? shipment.masterTrackingNumber ?? shipment.secondaryTrackingNumber ?? null;
}

/**
 * Method, tracking number, and tracking link for a shipped shipment. The method surfaces
 * whenever it is known — mirroring the pending card — while the tracking number and link
 * appear only once tracking has been assigned.
 */
function toShipmentMethodView(shipment: Shipment): ShipmentMethodView {
  const trackingNumber = getTrackingNumber(shipment);
  return {
    method: resolveCarrierName(shipment) || NOT_PROVIDED,
    trackingNumber,
    trackingUrl: trackingNumber ? buildTrackingUrl(shipment, trackingNumber) : null,
  };
}

/** Formats the estimated delivery date for display; returns "-" when unavailable. */
function formatEstimatedDeliveryDate(shippingDetails: unknown): string {
  return formatUtcCalendarDay(readShippingDetailString(shippingDetails, "estimatedDeliveryDateUTC"));
}

export function formatContactName(address: Address): string {
  return address.name ?? NOT_PROVIDED;
}

export function formatContactEmail(address: Address): string {
  return address.email ?? NOT_PROVIDED;
}

export function formatContactPhone(address: Address): string {
  return address.phone ?? NOT_PROVIDED;
}

/** Returns [recipientName, formattedAddress] for two-line display per Figma. */
export function formatShippingAddressLines(address: Address): string[] {
  const name = address.name ?? NOT_PROVIDED;
  const addressParts = [
    address.address1,
    address.address2,
    address.address3,
    address.city,
    address.regionIso,
    address.postalCode,
    address.countryIso,
  ].filter(Boolean);

  const addressLine = addressParts.length > 0 ? addressParts.join(", ") : NOT_PROVIDED;
  return [name, addressLine];
}

/**
 * Carrier tracking URL builders. Each entry maps a lowercase carrier keyword to a URL
 * builder using the carrier's query parameter for the tracking number.
 *
 * URL formats sourced from carrier documentation and verified manually:
 *   FedEx:  https://eshipguy.com/tracking/ → param `trknbr`
 *   UPS:    https://www.ups.com/us/en/business-shipping/shipping/tracking-api.page → param `tracknum`
 *   USPS:   https://tools.usps.com/go/TrackConfirmAction → param `qtc_tLabels1`
 *   DHL:    https://www.dhl.com/us-en/home/tracking.html — param `tracking-id` manually verified;
 *           no official deep-link documentation found. If links break, check current URL format.
 *
 * When updating a URL, also update the corresponding assertion in shipmentHelpers.test.ts.
 */
const CARRIER_TRACKING_URLS = new Map<string, (trackingNumber: string) => string>([
  ["fedex", (tn) => `https://www.fedex.com/fedextrack/?trknbr=${tn}`],
  ["ups", (tn) => `https://www.ups.com/track?tracknum=${tn}`],
  ["usps", (tn) => `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${tn}`],
  ["dhl", (tn) => `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${tn}`],
]);

/**
 * Builds a tracking URL by matching the carrier name against known carriers.
 * Uses the carrier name from shippingDetails when available, falls back to shippingMethodCode.
 * Returns null if the carrier is unrecognized.
 */
function buildTrackingUrl(shipment: Shipment, trackingNumber: string): string | null {
  const carrierName = resolveCarrierName(shipment).toLowerCase();
  for (const [carrier, buildUrl] of CARRIER_TRACKING_URLS) {
    if (carrierName.includes(carrier)) {
      return buildUrl(trackingNumber);
    }
  }
  return null;
}

/** Resolves a human-readable carrier name, preferring shippingDetails then falling back to shippingMethodCode. */
function resolveCarrierName(shipment: Shipment): string {
  return readMethodName(shipment.destination?.shippingDetails) ?? shipment.shippingMethodCode ?? "";
}

/** Reads the human-readable method name from shippingDetails: the actual name once known, else the planned name. */
function readMethodName(shippingDetails: unknown): string | null {
  return (
    readShippingDetailString(shippingDetails, "actualShippingMethodUsed") ??
    readShippingDetailString(shippingDetails, "name") ??
    null
  );
}

/** Reads a string field from the untyped shippingDetails JSON; returns undefined for missing or non-string values. */
function readShippingDetailString(shippingDetails: unknown, key: string): string | undefined {
  if (!isRecord(shippingDetails)) return undefined;
  const value = shippingDetails[key];
  return typeof value === "string" ? value : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
